import React from "react";
import { motion } from "framer-motion";
import { CONSTANTS } from "../../data";
import { X, Trophy, Lock } from "lucide-react";
import { useGameStateContext } from "../../GameStateProvider";

interface ChallengesModalProps {
  show: boolean;
  onClose: () => void;
  masterChefChallenge: any;
  startChallenge: (challengeId: string) => void;
}

const ChallengesModal: React.FC<ChallengesModalProps> = ({
  show,
  onClose,
  masterChefChallenge,
  startChallenge,
}) => {
  const { state } = useGameStateContext();
  const { level, discoveredRecipes, inventory, legendaryUnlocks } = state;

  if (!show) return null;

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getTimeRemaining = () => {
    if (!masterChefChallenge.active) return null;
    const elapsed = Date.now() - masterChefChallenge.startTime;
    const remaining = Math.max(0, masterChefChallenge.duration - elapsed);
    return remaining;
  };

  const timeRemaining = getTimeRemaining();

  const checkPrerequisites = (challenge) => {
    if (!challenge.prerequisites) return { met: true, message: '' };

    const { level: reqLevel, recipe: reqRecipe, item: reqItem, unlocks: reqUnlock } = challenge.prerequisites;

    if (reqLevel && level < reqLevel) {
      return { met: false, message: `Requires Level ${reqLevel}` };
    }
    if (reqRecipe && !discoveredRecipes.includes(reqRecipe)) {
      const recipeName = CONSTANTS.RECIPES[reqRecipe]?.result.name || reqRecipe;
      return { met: false, message: `Requires recipe: ${recipeName}` };
    }
    if (reqItem && !(inventory[reqItem] > 0)) {
      const itemName = CONSTANTS.ITEMS[reqItem]?.name || reqItem;
      return { met: false, message: `Requires item: ${itemName}` };
    }
    if (reqUnlock && !legendaryUnlocks[reqUnlock]) {
        const unlockName = reqUnlock.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        return { met: false, message: `Requires: ${unlockName}` };
    }

    return { met: true, message: '' };
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-2 sm:p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-surface rounded-lg w-full max-w-md max-h-[95vh] sm:max-h-[90vh] flex flex-col shadow-xl border border-border-color"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 sm:p-4 border-b border-border-color flex justify-between items-center flex-shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-text-primary flex items-center gap-2"><Trophy /> Master Chef Challenges</h2>
          <button 
            onClick={onClose} 
            className="text-text-secondary hover:text-text-primary p-2 rounded-full hover:bg-bg-main min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>
        <div className="modal-content p-3 sm:p-4 overflow-y-auto space-y-3 sm:space-y-4 flex-1">
          {masterChefChallenge.active && (
            <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-200 mb-4">
              <h3 className="font-bold text-blue-800 text-sm sm:text-base">Active Challenge</h3>
              <p className="text-xs sm:text-sm text-blue-600 mb-2 break-words">
                {CONSTANTS.MASTER_CHEF_CHALLENGES[masterChefChallenge.id]?.name}
              </p>
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs sm:text-sm">Progress:</span>
                <span className="font-bold text-sm">
                  {masterChefChallenge.progress}/{masterChefChallenge.goal}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${Math.min(100, (masterChefChallenge.progress / masterChefChallenge.goal) * 100)}%` 
                  }}
                />
              </div>
              {timeRemaining !== null && (
                <div className="text-xs sm:text-sm text-blue-600">
                  Time remaining: {formatDuration(timeRemaining)}
                </div>
              )}
            </div>
          )}
          
          {Object.values(CONSTANTS.MASTER_CHEF_CHALLENGES).map((challenge: any) => {
            const prereqStatus = checkPrerequisites(challenge);
            const isUnlocked = prereqStatus.met;
            const isActive = masterChefChallenge.active && masterChefChallenge.id === challenge.id;
            const anotherActive = masterChefChallenge.active && masterChefChallenge.id !== challenge.id;
            return (
              <div key={challenge.id} className={`bg-bg-main p-3 sm:p-4 rounded-lg border border-border-color ${!isUnlocked ? 'opacity-60' : ''}`}>
                <h3 className="font-bold text-text-primary flex items-center gap-2 text-sm sm:text-base">
                  {!isUnlocked && <Lock size={16} />}
                  {challenge.name}
                </h3>
                <p className="text-xs sm:text-sm text-text-secondary mb-2 break-words">{challenge.description}</p>
                <p className="text-xs sm:text-sm font-semibold text-text-secondary mb-2">
                  Duration: {formatDuration(challenge.duration)}
                </p>
                <p className="text-xs sm:text-sm font-semibold text-text-secondary break-words">
                  Reward: {challenge.reward.type === 'coins' 
                    ? `${challenge.reward.amount} coins` 
                    : challenge.reward.description}
                </p>
                <button
                  onClick={() => startChallenge(challenge.id)}
                  disabled={anotherActive || !isUnlocked}
                  className={`w-full mt-3 font-bold py-3 rounded-lg transition-colors min-h-[44px] text-sm sm:text-base
                    ${isActive
                      ? "bg-blue-400 text-white cursor-default"
                      : anotherActive || !isUnlocked
                        ? "bg-gray-400 text-white cursor-not-allowed"
                        : "bg-primary-action text-white hover:bg-accent"}
                  `}
                >
                  {isActive
                    ? 'In Progress'
                    : !isUnlocked
                      ? prereqStatus.message
                      : 'Start Challenge'}
                </button>
              </div>
            );
          })}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ChallengesModal;
